
import React, { useState, useCallback } from "react";
import { formatDate } from "@/utils/dateUtils";
import { MessageThread } from "@/services/messages";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Calendar, Search, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface MessageListProps {
  threads: MessageThread[];
  isLoading: boolean;
  onRefresh?: () => void;
}

const MessageList: React.FC<MessageListProps> = ({
  threads,
  isLoading,
  onRefresh
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  
  // Filter threads based on search query - memoized to avoid redundant filters
  const filteredThreads = React.useMemo(() => {
    if (!searchQuery.trim()) return threads;
    
    const searchLower = searchQuery.toLowerCase();
    return threads.filter(thread => {
      const usernameMatch = thread.username?.toLowerCase().includes(searchLower);
      const reagentMatch = thread.reagent_name?.toLowerCase().includes(searchLower);
      const messageMatch = thread.last_message?.toLowerCase().includes(searchLower);
      
      return usernameMatch || reagentMatch || messageMatch;
    });
  }, [threads, searchQuery]);
  
  // Memoize navigation handler to prevent unnecessary re-renders
  const handleSelectThread = useCallback((userId: string, reagentId?: string) => {
    // Use "/conversation/" path for consistent navigation
    navigate(`/conversation/${userId}${reagentId ? `?reagentId=${reagentId}` : ''}`);
  }, [navigate]);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="relative mb-4">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            className="pl-8"
            placeholder="Search conversations..."
            disabled
          />
        </div>
        {[1, 2, 3].map((i) => (
          <Card key={i} className="opacity-50">
            <CardContent className="p-4">
              <div className="h-16 animate-pulse bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            className="pl-8"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {onRefresh && (
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleManualRefresh}
            title="Refresh messages"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>

      {filteredThreads.length === 0 && (
        <div className="text-center py-8">
          {searchQuery ? (
            <div>
              <p className="text-muted-foreground">No conversations matching "{searchQuery}"</p>
              <button 
                className="text-primary text-sm mt-2 hover:underline"
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </button>
            </div>
          ) : (
            <div>
              <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p className="text-muted-foreground">No messages yet</p>
            </div>
          )}
        </div>
      )}

      {filteredThreads.length > 0 && (
        <div className="pr-3">
          <div className="space-y-3">
            {filteredThreads.map((thread) => (
              <Card 
                key={`${thread.user_id}-${thread.reagent_id || 'general'}`}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleSelectThread(thread.user_id, thread.reagent_id)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{thread.username || 'Unknown User'}</h3>
                      {thread.reagent_name ? (
                        <p className="text-sm text-muted-foreground">
                          Re: {thread.reagent_name}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Direct message
                        </p>
                      )}
                      <p className="text-sm line-clamp-1 mt-1">
                        {thread.last_message || 'No messages'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(thread.last_message_at)}
                      </div>
                      {thread.unread_count > 0 && (
                        <Badge variant="destructive" className="mt-2">
                          {thread.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(MessageList);
